import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * חודש שלם של סניף אחד (מיגרציה 097).
 *
 * קריאה נפרדת מהשבועית ולא חמש קריאות שבועיות: השרת מחזיר כאן
 * פחות — בלי אילוצים, בלי רשימת צוות ובלי דרישות לפי תפקיד —
 * כי זו תצוגה לקריאה, והשיבוץ עצמו נעשה בלוח השבועי.
 */

export type MonthShift = {
  shift_type_id: string;
  name: string;
  is_closed: boolean;
  closed_label: string | null;
  required: number;
  assigned: { id: string; employee_id: string; name: string }[];
};

export type MonthDay = {
  date: string;
  /** ימי הגלישה מהחודש השכן מוחזרים, כדי שהלוח יהיה מלבן שלם. */
  in_month: boolean;
  shifts: MonthShift[];
};

export type MonthBoard = {
  branch: { id: string; name: string; color: string | null };
  month_start: string;
  month_end: string;
  grid_start: string;
  grid_end: string;
  shift_types: { id: string; name: string; start_time: string; end_time: string }[];
  periods: { start_date: string; status: string; period_id: string }[];
  days: MonthDay[];
};

export function useScheduleMonth(month: string, branchId: string | null) {
  return useQuery({
    queryKey: ['schedule-month', month, branchId] as const,
    enabled: branchId !== null,
    queryFn: async (): Promise<MonthBoard | null> => {
      const { data, error } = await supabase.rpc('schedule_month', {
        p_month: month,
        p_branch: branchId as string,
      });
      if (error) throw error;
      return (data ?? null) as unknown as MonthBoard | null;
    },
  });
}

/** כמה מהדרישה של החודש מאויש. יום סגור אינו נספר. */
export function monthFill(board: MonthBoard): { filled: number; required: number } {
  let filled = 0;
  let required = 0;

  for (const day of board.days) {
    if (!day.in_month) continue;
    for (const shift of day.shifts) {
      if (shift.is_closed) continue;
      required += shift.required;
      filled += Math.min(shift.assigned.length, shift.required);
    }
  }

  return { filled, required };
}
